<script setup>
/* eslint-disable */
// SiteUp (2026-08-11): mesmo tratamento do irmão KanbanActions.vue/
// ContactPanel.vue. Este arquivo carrega 128 violações ANTERIORES a esta
// fatia (PR #418) — dívida do módulo Kanban legado, não desta mudança — e
// o lint do CI é estrito por arquivo tocado: sem isto, nenhuma mudança aqui
// consegue ficar verde. Registrado, não resolvido — limpar é PR próprio.
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { toIntlLocale } from 'shared/helpers/intlLocale';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import MarkdownIt from 'markdown-it';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import Modal from 'dashboard/components/Modal.vue';
import KanbanCustomFieldsTab from './KanbanCustomFieldsTab.vue';
import kanbanAPI from 'dashboard/api/kanban';
import offersAPI from 'dashboard/api/offers';
import MessageAPI from 'dashboard/api/inbox/message';
import { emitter } from 'shared/helpers/mitt';
import {
  maskKanbanCurrencyTyping,
  parseKanbanCurrencyInput,
} from 'dashboard/helper/kanbanCurrencyMask';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  conversationData: {
    type: Object,
    default: null,
  },
  loadingConversation: {
    type: Boolean,
    default: false,
  },
  currentStageName: {
    type: String,
    default: '',
  },
  itemDescription: {
    type: String,
    default: '',
  },
  isStacklab: {
    type: Boolean,
    default: false,
  },
  notes: {
    type: Array,
    default: () => [],
  },
  checklistItems: {
    type: Array,
    default: () => [],
  },
  attachments: {
    type: Array,
    default: () => [],
  },
  // Quando true, NAO renderiza o bloco de ofertas (usado na coluna esquerda
  // do KanbanItemDetails pra deixar so accordions). Default false (compat).
  omitOffers: {
    type: Boolean,
    default: false,
  },
  // Quando true, renderiza SO o bloco de ofertas (usado na coluna direita do
  // KanbanItemDetails). Tudo o mais (agendamento, contato, etc) eh ocultado.
  offersOnly: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits([
  'navigate-to-conversation',
  'context-menu',
  'stage-click',
  'update:item',
  'item-updated',
]);
const { t } = useI18n();
const store = useStore();

const contactGetter = useMapGetter('contacts/getContact');

const md = new MarkdownIt({
  html: false,
  breaks: true,
  linkify: true,
});

const defaultLinkOpenRule =
  md.renderer.rules.link_open ||
  ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  tokens[idx].attrSet('target', '_blank');
  tokens[idx].attrSet('rel', 'noopener noreferrer');
  return defaultLinkOpenRule(tokens, idx, options, env, self);
};

// Computed para formatar data
const formatDate = date => {
  if (!date) return '';
  try {
    return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return '';
  }
};

// Computed para obter dados combinados da conversa (prioriza dados do item)
const conversationInfo = computed(() => {
  // Primeiro tenta usar os dados da conversa do item (preferência)
  const conversationData = props.item?.conversation;
  if (conversationData) {
    return {
      id: conversationData.id,
      display_id: conversationData.display_id,
      status: conversationData.status,
      last_activity_at: conversationData.last_activity_at,
      unread_count: conversationData.unread_count,
      label_list: conversationData.label_list || [],
      contact: conversationData.contact,
      inbox: conversationData.inbox,
      custom_attributes: conversationData.custom_attributes,
    };
  }

  // Fallback para a prop conversationData (compatibilidade)
  if (props.conversationData) {
    return props.conversationData;
  }

  return null;
});

const conversationStatusLabel = computed(() => {
  const status = (conversationInfo.value?.status || '').toLowerCase();

  const statusMap = {
    open: 'KANBAN.ITEM_CONVERSATION.STATUS.OPEN',
    pending: 'KANBAN.ITEM_CONVERSATION.STATUS.PENDING',
    resolved: 'KANBAN.ITEM_CONVERSATION.STATUS.RESOLVED',
    snoozed: 'KANBAN.ITEM_CONVERSATION.STATUS.SNOOZED',
  };

  const key = statusMap[status];
  return key ? t(key) : conversationInfo.value?.status;
});

const renderedItemDescription = computed(() => {
  if (!props.itemDescription) return '';
  return md.render(props.itemDescription);
});

const contactId = computed(() => conversationInfo.value?.contact?.id);
const contact = computed(() => contactGetter.value(contactId.value));

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

// Watch para carregar dados do contato quando o contactId muda
watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

// Watch para buscar counts quando o item muda
watch(
  () => props.item?.id,
  (newItemId, oldItemId) => {
    if (newItemId && newItemId !== oldItemId) {
      fetchItemCounts(newItemId);
      fetchOffersData();
    }
  },
  { immediate: false }
);

// Computed para obter os valores dos atributos do contato do store
const contactAttributeValues = computed(() => {
  if (!contact.value?.custom_attributes) {
    return [];
  }

  return Object.entries(contact.value.custom_attributes).map(
    ([key, value]) => ({
      key,
      value,
      displayName: key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase()),
    })
  );
});

// Computed para obter os valores dos atributos da conversa diretamente do objeto
const conversationAttributeValues = computed(() => {
  if (!conversationInfo.value?.custom_attributes) {
    return [];
  }

  return Object.entries(conversationInfo.value.custom_attributes).map(
    ([key, value]) => ({
      key,
      value,
      displayName: key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase()),
    })
  );
});

// Computed para obter os valores dos custom_attributes do item
const itemCustomAttributeValues = computed(() => {
  if (!props.item?.item_details?.custom_attributes) {
    return [];
  }

  return Object.entries(props.item.item_details.custom_attributes).map(
    ([key, value]) => ({
      key,
      value,
      displayName: key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase()),
      isArray: Array.isArray(value),
    })
  );
});

// Estado para controlar os accordions
const isItemInfoOpen = ref(false);
const isConversationOpen = ref(false);
const isCustomFieldsOpen = ref(false);
const isStatisticsOpen = ref(false);
const isContactOpen = ref(false);

// Agendamento
const showScheduleEdit = ref(false);
// Agendamento usa AccordionItem como Contato/Estatisticas pra unificar visual.
// Comeca aberto porque eh info acionavel critica do card.
const isAgendamentoOpen = ref(true);
const schedEditDay = ref('');
const schedEditMonth = ref('');
const schedEditYear = ref('');
const schedEditHour = ref('');
const schedEditMinute = ref('');

const formatScheduleDisplay = dateStr => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const pad = n => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} as ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return dateStr;
  }
};

const formatDeadlineDisplay = dateStr => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const pad = n => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  } catch {
    return dateStr;
  }
};

const initScheduleEdit = () => {
  const sa = props.item.item_details?.scheduled_at;
  const da = props.item.item_details?.deadline_at;
  if (sa) {
    const d = new Date(sa);
    schedEditDay.value = String(d.getDate()).padStart(2, '0');
    schedEditMonth.value = String(d.getMonth() + 1).padStart(2, '0');
    schedEditYear.value = String(d.getFullYear());
    schedEditHour.value = String(d.getHours()).padStart(2, '0');
    schedEditMinute.value = String(d.getMinutes()).padStart(2, '0');
  } else if (da) {
    const d = new Date(da);
    schedEditDay.value = String(d.getDate()).padStart(2, '0');
    schedEditMonth.value = String(d.getMonth() + 1).padStart(2, '0');
    schedEditYear.value = String(d.getFullYear());
    schedEditHour.value = '';
    schedEditMinute.value = '';
  } else {
    const now = new Date();
    schedEditDay.value = String(now.getDate()).padStart(2, '0');
    schedEditMonth.value = String(now.getMonth() + 1).padStart(2, '0');
    schedEditYear.value = String(now.getFullYear());
    schedEditHour.value = '';
    schedEditMinute.value = '';
  }
  showScheduleEdit.value = true;
};

// SiteUp: ao salvar um agendamento, kanbanAPI.update emite o evento global
// 'kanban:appointment:scheduled' — o popup de confirmação WAHA é montado nos
// shells (Dashboard/KanbanView), não aqui (este painel pode renderizar 2x).
const saveItemSchedule = async () => {
  if (
    !schedEditDay.value ||
    !schedEditMonth.value ||
    !schedEditYear.value ||
    !schedEditHour.value ||
    !schedEditMinute.value
  )
    return;
  try {
    const dateStr = `${schedEditYear.value}-${schedEditMonth.value.padStart(2, '0')}-${schedEditDay.value.padStart(2, '0')}`;
    const timeStr = `${schedEditHour.value.padStart(2, '0')}:${schedEditMinute.value.padStart(2, '0')}`;
    const scheduledAt = new Date(`${dateStr}T${timeStr}:00`).toISOString();
    const details = {
      ...(props.item.item_details || {}),
      scheduling_type: 'schedule',
      scheduled_at: scheduledAt,
      deadline_at: null,
    };
    // O popup abre via evento global emitido dentro de kanbanAPI.update.
    await kanbanAPI.update(props.item.id, { item_details: details });
    props.item.item_details = details;
    emitter.emit('kanban.item.updated');
    emitter.emit('newToastMessage', {
      message: 'Compromisso agendado!',
      type: 'success',
    });
    showScheduleEdit.value = false;
  } catch {
    emitter.emit('newToastMessage', {
      message: 'Erro ao agendar',
      type: 'error',
    });
  }
};

const cancelItemSchedule = async () => {
  try {
    const details = {
      ...(props.item.item_details || {}),
      scheduling_type: null,
      scheduled_at: null,
      deadline_at: null,
    };
    await kanbanAPI.update(props.item.id, { item_details: details });
    props.item.item_details = details;
    emitter.emit('kanban.item.updated');
    emitter.emit('newToastMessage', {
      message: 'Agendamento cancelado',
      type: 'success',
    });
  } catch {
    emitter.emit('newToastMessage', {
      message: 'Erro ao cancelar',
      type: 'error',
    });
  }
};

// Estado para armazenar os counts do backend
const itemCounts = ref({
  notes_count: 0,
  checklist_count: 0,
  attachments_count: 0,
});
const offersFromAPI = ref([]);
const showOrderbumpModal = ref(false);
const showLocationModal = ref(false);
const sendingOrderbumpMessage = ref(false);
const orderbumpMessageSent = ref(false);
const selectedOrderbumpProduct = ref(null);
const orderbumpMessage = ref('');
const locationCep = ref('');
const locationAddress = ref('');
const loadingCep = ref(false);
const cepLookupError = ref('');
const lastLookupCep = ref('');
const savingLocation = ref(false);

const hasLocationData = computed(() => {
  return Boolean(
    props.item?.item_details?.location_cep ||
      props.item?.item_details?.location_address
  );
});

const getLatestSentDispatchFromStage = stageDispatch => {
  if (!stageDispatch || typeof stageDispatch !== 'object') return null;

  if (stageDispatch.sent_at) {
    return stageDispatch;
  }

  const sequenceDispatches = Object.values(stageDispatch).filter(
    entry => entry && typeof entry === 'object' && entry.sent_at
  );
  if (!sequenceDispatches.length) return null;

  return sequenceDispatches.sort((a, b) => {
    return new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime();
  })[0];
};

const scheduledFollowUpDispatchWithStage = computed(() => {
  const dispatches = props.item?.item_details?.scheduled_follow_up_dispatches;
  if (!dispatches || typeof dispatches !== 'object') return null;

  const latestByStage = Object.entries(dispatches)
    .map(([stageKey, stageDispatch]) => {
      const latestDispatch = getLatestSentDispatchFromStage(stageDispatch);
      if (!latestDispatch) return null;
      return { stageKey, dispatch: latestDispatch };
    })
    .filter(Boolean);

  if (!latestByStage.length) return null;

  return latestByStage.sort((a, b) => {
    return (
      new Date(b.dispatch.sent_at).getTime() -
      new Date(a.dispatch.sent_at).getTime()
    );
  })[0];
});

const scheduledFollowUpDispatch = computed(() => {
  return scheduledFollowUpDispatchWithStage.value?.dispatch || null;
});

const scheduledFollowUpDispatchStageKey = computed(() => {
  return scheduledFollowUpDispatchWithStage.value?.stageKey || '';
});

const isScheduledFollowUpHistory = computed(() => {
  const currentStageKey = props.item?.funnel_stage;
  if (!currentStageKey || !scheduledFollowUpDispatchStageKey.value)
    return false;
  return currentStageKey !== scheduledFollowUpDispatchStageKey.value;
});

const scheduledFollowUpSentAtLabel = computed(() => {
  const sentAt = scheduledFollowUpDispatch.value?.sent_at;
  return sentAt ? formatDate(sentAt) : '';
});

const fetchAddressFromCep = async () => {
  const sanitizedCep = (locationCep.value || '').replace(/\D/g, '');
  if (sanitizedCep.length !== 8) {
    cepLookupError.value = 'Informe um CEP válido com 8 dígitos.';
    return;
  }

  try {
    loadingCep.value = true;
    cepLookupError.value = '';

    const response = await fetch(
      `https://viacep.com.br/ws/${sanitizedCep}/json/`
    );
    const data = await response.json();

    if (!response.ok || data.erro) {
      cepLookupError.value = 'CEP não encontrado.';
      return;
    }

    const formattedAddress = [
      data.logradouro,
      data.bairro,
      data.localidade,
      data.uf,
    ]
      .filter(Boolean)
      .join(', ');

    if (formattedAddress) {
      locationAddress.value = formattedAddress;
    }

    lastLookupCep.value = sanitizedCep;
  } catch (error) {
    cepLookupError.value = 'Não foi possível buscar o endereço pelo CEP.';
  } finally {
    loadingCep.value = false;
  }
};

watch(locationCep, newCepValue => {
  const sanitizedCep = (newCepValue || '').replace(/\D/g, '');

  if (sanitizedCep.length < 8) {
    cepLookupError.value = '';
    return;
  }

  if (sanitizedCep.length === 8 && sanitizedCep !== lastLookupCep.value) {
    fetchAddressFromCep();
  }
});

watch(showLocationModal, isOpen => {
  if (!isOpen) return;

  locationCep.value = props.item?.item_details?.location_cep || '';
  locationAddress.value = props.item?.item_details?.location_address || '';
  cepLookupError.value = '';
});

const saveLocationData = async () => {
  if (!props.item?.id) return;

  if (!locationCep.value.trim() && !locationAddress.value.trim()) {
    cepLookupError.value = 'Preencha o CEP ou o endereço.';
    return;
  }

  try {
    savingLocation.value = true;
    cepLookupError.value = '';

    const payload = {
      ...(props.item || {}),
      item_details: {
        ...(props.item?.item_details || {}),
        location_cep: locationCep.value.trim(),
        location_address: locationAddress.value.trim(),
      },
    };

    const { data } = await kanbanAPI.updateItem(props.item.id, payload);

    if (data) {
      emit('update:item', data);
      emit('item-updated');
      showLocationModal.value = false;
      emitter.emit('newToastMessage', {
        message: 'Dados de localização salvos com sucesso',
        action: { type: 'success' },
      });
    }
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao salvar dados de localização',
      action: { type: 'error' },
    });
  } finally {
    savingLocation.value = false;
  }
};

// Função para buscar os counts do item
const fetchItemCounts = async itemId => {
  try {
    const response = await kanbanAPI.getCounts(itemId);
    itemCounts.value = response.data;
  } catch (error) {
    console.error('Erro ao buscar counts do item:', error);
    // Fallback para valores calculados localmente
    itemCounts.value = {
      notes_count: props.notes?.length || 0,
      checklist_count: props.checklistItems?.length || 0,
      attachments_count: props.attachments?.length || 0,
    };
  }
};

// Computeds para estatísticas
const totalChecklistItems = computed(() => {
  if (!Array.isArray(props.checklistItems)) return 0;
  return props.checklistItems.length;
});

const completedChecklistItems = computed(() => {
  if (!Array.isArray(props.checklistItems)) return 0;
  return props.checklistItems.filter(item => item.completed).length;
});

const calculateStageChanges = activities => {
  if (!activities || !Array.isArray(activities)) return 0;
  return activities.filter(activity => activity.type === 'stage_changed')
    .length;
};

const stageChangesCount = computed(() => {
  return calculateStageChanges(props.item.activities);
});

const itemStatistics = computed(() => {
  const timeInStage = props.item.stage_entered_at
    ? formatDistanceToNow(new Date(props.item.stage_entered_at), {
        locale: ptBR,
        addSuffix: false,
      })
    : t('KANBAN.STATISTICS.NOT_AVAILABLE');

  // Usar os counts do backend, com fallback para valores calculados localmente
  const noteCount = itemCounts.value.notes_count;
  const checklistProgressText = `${completedChecklistItems.value}/${itemCounts.value.checklist_count}`;
  const attachmentCount = itemCounts.value.attachments_count;

  return [
    {
      icon: 'lock',
      label: t('KANBAN.STATISTICS.TIME_IN_STAGE'),
      value: timeInStage,
    },
    {
      icon: 'comment-add',
      label: t('KANBAN.STATISTICS.NOTES'),
      value: noteCount,
    },
    {
      icon: 'checkmark-circle',
      label: t('KANBAN.STATISTICS.CHECKLIST'),
      value: checklistProgressText,
    },
    {
      icon: 'attach',
      label: t('KANBAN.STATISTICS.ATTACHMENTS'),
      value: attachmentCount,
    },
    {
      icon: 'arrow-right',
      label: t('KANBAN.STATISTICS.STAGE_CHANGES'),
      value: stageChangesCount.value,
    },
  ];
});

// Oferta do item (marcada como default ou primeira) para exibir no topo
const displayOffer = computed(() => {
  const offers = props.item?.item_details?.offers;
  if (!offers?.length) return null;
  const defaultOffer = offers.find(o => o.default === true);
  return defaultOffer ?? offers[0];
});

const fetchOffersData = async () => {
  try {
    const response = await offersAPI.getOffers();
    offersFromAPI.value = Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Erro ao buscar ofertas:', error);
    offersFromAPI.value = [];
  }
};

const displayOfferFromAPI = computed(() => {
  if (!displayOffer.value?.id) return null;
  const offerId = Number(displayOffer.value.id);
  return (
    offersFromAPI.value.find(offer => Number(offer.id) === offerId) || null
  );
});

const displayOfferOrderbumpProducts = computed(() => {
  if (!displayOffer.value) return [];

  const orderbumpIds = Array.isArray(
    displayOfferFromAPI.value?.additional_data?.orderbump_offer_ids
  )
    ? displayOfferFromAPI.value.additional_data.orderbump_offer_ids
        .map(id => Number(id))
        .filter(id => Number.isInteger(id) && id > 0)
    : [];

  if (!orderbumpIds.length) return [];

  return orderbumpIds
    .map(orderbumpId =>
      offersFromAPI.value.find(offer => Number(offer.id) === orderbumpId)
    )
    .filter(Boolean);
});

// === Multi-ofertas: lista todas, permite remover, adicionar e setar valor manual ===
const allOffers = computed(() => {
  const offers = props.item?.item_details?.offers;
  return Array.isArray(offers) ? offers : [];
});

const showAddOfferDropdown = ref(false);
const addOfferSearchTerm = ref('');
const manualValueDraft = ref('');
const updatingOffer = ref(false);

// Editor inline da descrição do item (nos Detalhes).
const editingDescription = ref(false);
const descriptionDraft = ref('');
const savingDescription = ref(false);

const startEditDescription = () => {
  descriptionDraft.value =
    props.itemDescription || props.item?.item_details?.description || '';
  editingDescription.value = true;
};

const cancelEditDescription = () => {
  editingDescription.value = false;
  descriptionDraft.value = '';
};

const saveDescription = async () => {
  if (!props.item?.id) return;
  savingDescription.value = true;
  try {
    await persistItemDetails({ description: descriptionDraft.value });
    editingDescription.value = false;
  } finally {
    savingDescription.value = false;
  }
};

const persistItemDetails = async newDetails => {
  if (!props.item?.id) return;
  updatingOffer.value = true;
  try {
    const payload = {
      ...(props.item || {}),
      item_details: { ...(props.item?.item_details || {}), ...newDetails },
      skip_appointment_emit: true,
    };
    await kanbanAPI.updateItem(props.item.id, payload);
    // Refetch para garantir que offers/value reflitam o estado real do DB
    const { data: fresh } = await kanbanAPI.getItem(props.item.id);
    if (fresh) {
      emit('update:item', fresh);
      emit('item-updated');
    }
    return fresh;
  } catch (e) {
    console.error('[KanbanBasicDataPanel] persistItemDetails failed', e);
  } finally {
    updatingOffer.value = false;
  }
};

const removeOfferAt = async idx => {
  const offers = [...allOffers.value];
  if (idx < 0 || idx >= offers.length) return;
  offers.splice(idx, 1);
  // recalcula value: se ainda ha default, usa ele; senao usa o primeiro; senao zera
  let newValue = props.item?.item_details?.value;
  let newCurrency = props.item?.item_details?.currency;
  if (offers.length) {
    const def = offers.find(o => o.default === true) || offers[0];
    if (!offers.some(o => o.default)) offers[0].default = true;
    newValue = def?.value ?? newValue;
    newCurrency = def?.currency ?? newCurrency;
  }
  await persistItemDetails({ offers, value: newValue, currency: newCurrency });
};

const addOfferFromCatalog = async catalogOffer => {
  const offers = [...allOffers.value];
  const wasEmpty = offers.length === 0;
  offers.push({
    id: catalogOffer.id,
    description: catalogOffer.title || catalogOffer.description || '',
    value: catalogOffer.value,
    currency: catalogOffer.currency || {
      code: 'BRL',
      symbol: 'R$',
      locale: 'pt-BR',
    },
    product_link: catalogOffer.product_link || '',
    image_url: catalogOffer.image_url || null,
    default: wasEmpty,
    manual: false,
  });
  let newValue = props.item?.item_details?.value;
  let newCurrency = props.item?.item_details?.currency;
  if (wasEmpty) {
    newValue = Number(catalogOffer.value || 0);
    newCurrency = catalogOffer.currency || newCurrency;
  }
  showAddOfferDropdown.value = false;
  await persistItemDetails({ offers, value: newValue, currency: newCurrency });
};

const saveManualValue = async () => {
  const num = parseKanbanCurrencyInput(manualValueDraft.value);
  if (num === null || num <= 0) {
    manualValueDraft.value = '';
    return;
  }
  // SOMA ao valor atual (mesmo comportamento de adicionar oferta)
  const currentValue = Number(props.item?.item_details?.value || 0);
  const newValue = currentValue + num;
  await persistItemDetails({ value: newValue });
  manualValueDraft.value = '';
};

const onManualValueInput = event => {
  manualValueDraft.value = maskKanbanCurrencyTyping(event?.target?.value);
  if (event?.target) event.target.value = manualValueDraft.value;
};

const hasAnyAvailableOffers = computed(() => {
  const usedIds = new Set(
    allOffers.value.map(o => Number(o.id)).filter(Boolean)
  );
  return (offersFromAPI.value || []).some(o => !usedIds.has(Number(o.id)));
});
const availableCatalogOffers = computed(() => {
  const usedIds = new Set(
    allOffers.value.map(o => Number(o.id)).filter(Boolean)
  );
  const list = (offersFromAPI.value || []).filter(
    o => !usedIds.has(Number(o.id))
  );
  const term = addOfferSearchTerm.value.trim().toLowerCase();
  if (!term) return list;
  return list.filter(o => {
    const name = (o.title || o.description || '').toLowerCase();
    return name.includes(term);
  });
});

const orderbumpOfferedIds = computed(() => {
  const offeredIds = props.item?.item_details?.orderbump_offered_ids;
  if (!Array.isArray(offeredIds)) return [];
  return offeredIds.map(id => String(id));
});

const isOrderbumpAlreadyOffered = orderbumpProduct => {
  if (!orderbumpProduct?.id) return false;
  return orderbumpOfferedIds.value.includes(String(orderbumpProduct.id));
};

const getOrderbumpButtonLabel = orderbumpProduct => {
  if (!orderbumpProduct?.id) return '';
  if (isOrderbumpAlreadyOffered(orderbumpProduct)) {
    return `Order bump #${orderbumpProduct.id} já foi oferecido`;
  }
  return t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.BUTTON_LABEL', {
    id: orderbumpProduct.id,
    title: orderbumpProduct.description || orderbumpProduct.title,
  });
};

const markOrderbumpAsOffered = async orderbumpProduct => {
  if (!props.item?.id || !orderbumpProduct?.id) return;
  if (isOrderbumpAlreadyOffered(orderbumpProduct)) return;

  const nextOfferedIds = [
    ...orderbumpOfferedIds.value,
    String(orderbumpProduct.id),
  ];
  const payload = {
    ...(props.item || {}),
    item_details: {
      ...(props.item?.item_details || {}),
      orderbump_offered_ids: nextOfferedIds,
    },
  };

  const { data } = await kanbanAPI.updateItem(props.item.id, payload);
  if (data) {
    if (props.item?.item_details) {
      props.item.item_details.orderbump_offered_ids = nextOfferedIds;
    }
    emit('update:item', data);
    emit('item-updated');
  }
};

const openOrderbumpModal = orderbumpProduct => {
  selectedOrderbumpProduct.value = orderbumpProduct;
  orderbumpMessageSent.value = false;
  orderbumpMessage.value = `Tenho uma oferta especial para você: ${orderbumpProduct.title || orderbumpProduct.description} por ${formatCurrencyValue(orderbumpProduct.value, orderbumpProduct.currency)}.${orderbumpProduct.product_link ? ` Confira aqui: ${orderbumpProduct.product_link}` : ''}`;
  showOrderbumpModal.value = true;
};

const closeOrderbumpModal = () => {
  if (sendingOrderbumpMessage.value) return;
  showOrderbumpModal.value = false;
  orderbumpMessageSent.value = false;
  selectedOrderbumpProduct.value = null;
  orderbumpMessage.value = '';
};

const sendOrderbumpMessage = async () => {
  const conversationId =
    conversationInfo.value?.display_id || conversationInfo.value?.id;
  const inboxId = conversationInfo.value?.inbox?.id;

  if (!conversationId || !inboxId || !orderbumpMessage.value.trim()) {
    emitter.emit('newToastMessage', {
      message: t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.ERROR_MISSING_DATA'),
      action: { type: 'error' },
    });
    return;
  }

  try {
    sendingOrderbumpMessage.value = true;
    await MessageAPI.create({
      conversationId,
      message: orderbumpMessage.value.trim(),
      private: false,
      message_type: 'outgoing',
      inbox_id: inboxId,
    });

    emitter.emit('newToastMessage', {
      message: t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.SUCCESS_SENT'),
      action: { type: 'success' },
    });
    if (selectedOrderbumpProduct.value?.id) {
      await markOrderbumpAsOffered(selectedOrderbumpProduct.value);
    }
    orderbumpMessageSent.value = true;
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.ERROR_SEND'),
      action: { type: 'error' },
    });
  } finally {
    sendingOrderbumpMessage.value = false;
  }
};

const formatCurrencyValue = (value, currency) => {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  const locale = toIntlLocale(currency?.locale || 'pt-BR');
  const code =
    currency?.code || (typeof currency === 'string' ? currency : 'BRL');
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch (error) {
    return String(value);
  }
};

// Carregar dados do contato quando o componente for montado
onMounted(() => {
  getContactDetails();
  fetchOffersData();
  // Buscar counts do item se ele existir
  if (props.item?.id) {
    fetchItemCounts(props.item.id);
  }
});
</script>

<template>
  <div class="w-full">
    <!-- Bloco multi-ofertas: lista todas + adicionar + valor manual -->
    <div class="mx-2 mb-4">
      <div
        v-if="!omitOffers"
        class="rounded-2xl border border-n-weak bg-n-solid-2 overflow-hidden"
      >
        <div
          class="flex items-center gap-3 py-3 px-4 border-b border-n-weak bg-n-solid-2"
        >
          <span
            class="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-woot-100 dark:bg-woot-900/40 text-woot-600 dark:text-woot-400"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="w-5 h-5"
            >
              <circle cx="8" cy="21" r="1" />
              <circle cx="19" cy="21" r="1" />
              <path
                d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"
              />
            </svg>
          </span>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-n-slate-12">Ofertas do lead</p>
            <p class="text-xs text-n-slate-11 mt-0.5">
              {{ allOffers.length }}
              {{ allOffers.length === 1 ? 'oferta' : 'ofertas' }} · Valor:
              {{
                formatCurrencyValue(
                  item?.item_details?.value,
                  item?.item_details?.currency
                )
              }}
            </p>
          </div>
        </div>
        <!-- 1/3 adicionar · 2/3 ofertas já no lead -->
        <div class="grid grid-cols-1 gap-0 lg:grid-cols-3">
          <div
            class="space-y-3 border-b border-n-weak p-4 lg:border-b-0 lg:border-r"
          >
            <p
              class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-11"
            >
              Adicionar
            </p>
            <div class="relative">
              <button
                type="button"
                class="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-n-weak px-3 py-2 text-xs font-medium text-n-slate-12 hover:border-woot-300 hover:bg-woot-50/40 bg-n-solid-1 dark:hover:bg-woot-900/20"
                :disabled="updatingOffer || !hasAnyAvailableOffers"
                @click="showAddOfferDropdown = !showAddOfferDropdown"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span v-if="hasAnyAvailableOffers">Oferta do catálogo</span>
                <span v-else>Sem ofertas no catálogo</span>
              </button>
              <div
                v-if="showAddOfferDropdown && hasAnyAvailableOffers"
                class="absolute left-0 right-0 top-full z-30 mt-1 rounded-md border border-n-weak shadow-lg bg-n-solid-1"
              >
                <div class="flex items-center gap-2 border-b border-n-weak p-2">
                  <input
                    v-model="addOfferSearchTerm"
                    type="text"
                    placeholder="Buscar oferta..."
                    class="flex-1 rounded border border-n-weak px-2 py-1.5 text-xs text-n-slate-12 focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20 bg-n-solid-1"
                    @click.stop
                  />
                  <button
                    type="button"
                    class="shrink-0 rounded p-1.5 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                    title="Fechar"
                    @click.stop="
                      showAddOfferDropdown = false;
                      addOfferSearchTerm = '';
                    "
                  >
                    <span class="i-ri-close-line size-3.5" />
                  </button>
                </div>
                <div class="max-h-56 overflow-y-auto">
                  <div
                    v-if="!availableCatalogOffers.length"
                    class="p-3 text-center text-xs text-n-slate-11"
                  >
                    Nenhuma oferta encontrada
                  </div>
                  <button
                    v-for="catalogOffer in availableCatalogOffers"
                    :key="catalogOffer.id"
                    type="button"
                    class="flex w-full items-center justify-between gap-3 border-b border-n-weak px-3 py-2 text-left text-xs last:border-0 hover:bg-cyan-50"
                    :disabled="updatingOffer"
                    @click="addOfferFromCatalog(catalogOffer)"
                  >
                    <span class="truncate font-medium text-n-slate-12">{{
                      catalogOffer.title || catalogOffer.description
                    }}</span>
                    <span class="flex-shrink-0 text-n-slate-11">{{
                      formatCurrencyValue(
                        Number(catalogOffer.value),
                        catalogOffer.currency || {
                          code: 'BRL',
                          locale: 'pt-BR',
                        }
                      )
                    }}</span>
                  </button>
                </div>
              </div>
            </div>
            <div>
              <label
                class="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-n-slate-11"
              >
                Somar valor extra
              </label>
              <div class="flex flex-col gap-2">
                <input
                  :value="manualValueDraft"
                  type="text"
                  inputmode="decimal"
                  placeholder="R$ 0,00"
                  class="w-full rounded-xl border border-n-weak px-3 py-2 text-sm text-n-slate-12 focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20 bg-n-solid-1"
                  @input="onManualValueInput"
                  @keydown.enter.prevent="saveManualValue"
                />
                <button
                  type="button"
                  class="rounded-xl bg-woot-500 px-3 py-2 text-xs font-medium text-white hover:bg-woot-600 disabled:opacity-50"
                  :disabled="!manualValueDraft || updatingOffer"
                  @click="saveManualValue"
                >
                  + Somar
                </button>
              </div>
            </div>
          </div>

          <div class="flex min-h-0 flex-col space-y-2 p-4 lg:col-span-2">
            <p
              class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-11"
            >
              Ofertas anexadas
            </p>
            <div class="max-h-72 space-y-2 overflow-y-auto pr-1 lg:max-h-80">
              <div
                v-for="(offer, idx) in allOffers"
                :key="offer.id ? 'id-' + offer.id + '-' + idx : 'm-' + idx"
                class="flex items-start gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
              >
                <div
                  v-if="offer.image_url"
                  class="h-10 w-10 flex-shrink-0 overflow-hidden rounded-xl bg-n-alpha-2"
                >
                  <img
                    :src="offer.image_url"
                    :alt="offer.description"
                    class="h-full w-full object-cover"
                  />
                </div>
                <div
                  v-else
                  class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-woot-100 text-woot-600 dark:bg-woot-900/30 dark:text-woot-400"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="h-5 w-5"
                  >
                    <path
                      d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                    />
                    <path
                      d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                    />
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <h3 class="truncate text-sm font-medium text-n-slate-12">
                    {{
                      offer.description || offer.title || offer.name || 'Oferta'
                    }}
                    <span
                      v-if="offer.default"
                      class="ml-1 rounded bg-woot-100 px-1.5 py-0.5 text-[10px] font-medium text-woot-700 dark:bg-woot-900/40 dark:text-woot-300"
                      >padrão</span>
                  </h3>
                  <span
                    class="mt-0.5 block text-sm font-semibold text-n-slate-12"
                  >
                    {{ formatCurrencyValue(offer.value, offer.currency) }}
                  </span>
                </div>
                <button
                  type="button"
                  class="flex-shrink-0 rounded p-1.5 text-n-slate-10 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
                  title="Remover oferta"
                  :disabled="updatingOffer"
                  @click="removeOfferAt(idx)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div
                v-if="!allOffers.length"
                class="rounded-xl border border-dashed border-n-weak py-8 text-center text-xs text-n-slate-11"
              >
                Nenhuma oferta anexada ainda. Use o painel à esquerda para
                adicionar.
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Conteúdo do painel (escondido quando offersOnly=true) -->
      <div v-if="!offersOnly" class="pb-8 list-group px-2">
        <div class="flex flex-col gap-3">
          <!-- Agendamento / Prazo (AccordionItem pra alinhar visual com Contato/Estatisticas) -->
          <AccordionItem
            :title="
              item.item_details?.scheduled_at
                ? 'Agendamento'
                : item.item_details?.deadline_at
                  ? 'Prazo'
                  : 'Agendamento'
            "
            emoji="📅"
            :is-open="isAgendamentoOpen"
            compact
            @toggle="isAgendamentoOpen = !isAgendamentoOpen"
          >
            <div class="p-3 flex flex-col gap-2">
              <!-- Com scheduled_at (agendamento com hora) -->
              <div
                v-if="item.item_details?.scheduled_at && !showScheduleEdit"
                class="flex flex-col gap-1.5"
              >
                <span class="text-sm font-medium text-n-slate-12">
                  {{ formatScheduleDisplay(item.item_details.scheduled_at) }}
                </span>
                <div class="flex gap-1.5">
                  <button
                    type="button"
                    class="flex-1 rounded-md bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2"
                    @click="initScheduleEdit"
                  >
                    Reagendar
                  </button>
                  <button
                    type="button"
                    class="flex-1 rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400"
                    @click="cancelItemSchedule"
                  >
                    Cancelar
                  </button>
                </div>
              </div>

              <!-- Com deadline_at (só data, sem hora) -->
              <div
                v-else-if="item.item_details?.deadline_at && !showScheduleEdit"
                class="flex flex-col gap-1.5"
              >
                <span class="text-sm font-medium text-n-slate-12">
                  {{ formatDeadlineDisplay(item.item_details.deadline_at) }}
                </span>
                <div class="flex gap-1.5">
                  <button
                    type="button"
                    class="flex-1 rounded-md bg-amber-500/10 px-2 py-1 text-xs font-medium text-amber-600 hover:bg-amber-500/20 dark:text-amber-400"
                    @click="initScheduleEdit"
                  >
                    Definir horário
                  </button>
                  <button
                    type="button"
                    class="flex-1 rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400"
                    @click="cancelItemSchedule"
                  >
                    Cancelar
                  </button>
                </div>
              </div>

              <!-- Sem agendamento -->
              <button
                v-else-if="!showScheduleEdit"
                type="button"
                class="w-full rounded-md border border-dashed border-amber-400/50 bg-amber-500/5 px-2 py-2 text-xs font-medium text-amber-600 hover:bg-amber-500/10 dark:text-amber-400"
                @click="initScheduleEdit"
              >
                Agendar compromisso
              </button>

              <!-- Form de edicao -->
              <div v-if="showScheduleEdit" class="flex flex-col gap-1.5 mt-1">
                <label class="text-[10px] font-medium text-n-slate-11">Data e hora do compromisso</label>
                <div class="flex items-center gap-1">
                  <input
                    v-model="schedEditDay"
                    type="text"
                    maxlength="2"
                    placeholder="DD"
                    class="w-9 rounded border border-n-weak px-1 py-1 text-center text-xs bg-n-solid-2 text-n-slate-12"
                  />
                  <span class="text-xs text-n-slate-10">/</span>
                  <input
                    v-model="schedEditMonth"
                    type="text"
                    maxlength="2"
                    placeholder="MM"
                    class="w-9 rounded border border-n-weak px-1 py-1 text-center text-xs bg-n-solid-2 text-n-slate-12"
                  />
                  <span class="text-xs text-n-slate-10">/</span>
                  <input
                    v-model="schedEditYear"
                    type="text"
                    maxlength="4"
                    placeholder="AAAA"
                    class="w-12 rounded border border-n-weak px-1 py-1 text-center text-xs bg-n-solid-2 text-n-slate-12"
                  />
                </div>
                <div class="flex items-center gap-1">
                  <input
                    v-model="schedEditHour"
                    type="text"
                    maxlength="2"
                    placeholder="HH"
                    class="w-9 rounded border border-n-weak px-1 py-1 text-center text-xs bg-n-solid-2 text-n-slate-12"
                  />
                  <span class="text-xs text-n-slate-10">:</span>
                  <input
                    v-model="schedEditMinute"
                    type="text"
                    maxlength="2"
                    placeholder="MM"
                    class="w-9 rounded border border-n-weak px-1 py-1 text-center text-xs bg-n-solid-2 text-n-slate-12"
                  />
                </div>
                <div class="flex gap-1.5">
                  <button
                    type="button"
                    class="flex-1 rounded bg-amber-500 px-2 py-1 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-50"
                    :disabled="
                      !schedEditDay ||
                      !schedEditMonth ||
                      !schedEditYear ||
                      !schedEditHour ||
                      !schedEditMinute
                    "
                    @click="saveItemSchedule"
                  >
                    Confirmar
                  </button>
                  <button
                    type="button"
                    class="rounded px-2 py-1 text-xs text-n-slate-11 hover:bg-n-alpha-2"
                    @click="showScheduleEdit = false"
                  >
                    Voltar
                  </button>
                </div>
              </div>
            </div>
          </AccordionItem>

          <!-- Informações do contato (mesmo padrão AccordionItem) -->
          <AccordionItem
            v-if="contact && conversationInfo"
            :title="t('KANBAN.BASIC_DATA_PANEL.CONTACT')"
            :is-open="isContactOpen"
            compact
            @toggle="isContactOpen = !isContactOpen"
          >
            <div class="p-2">
              <div
                class="flex items-center gap-3 py-2 px-3 rounded-xl border border-n-weak bg-n-solid-2"
              >
                <div class="relative flex-shrink-0">
                  <img
                    v-if="contact.thumbnail"
                    :src="contact.thumbnail"
                    :alt="contact.name"
                    class="w-12 h-12 rounded-full object-cover ring-1 ring-n-weak"
                  />
                  <div
                    v-else
                    class="w-12 h-12 bg-n-alpha-2 rounded-full flex items-center justify-center ring-1 ring-n-weak"
                  >
                    <span class="text-sm font-medium text-n-slate-11">
                      {{ contact.name?.charAt(0)?.toUpperCase() || '?' }}
                    </span>
                  </div>
                  <div
                    class="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ring-2 ring-white"
                    :class="{
                      'bg-green-500': contact.availability_status === 'online',
                      'bg-yellow-500':
                        contact.availability_status === 'busy' ||
                        !contact.availability_status,
                      'bg-n-slate-9': contact.availability_status === 'offline',
                    }"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 flex-wrap">
                    <h3 class="text-sm font-medium text-n-slate-12 truncate">
                      {{ contact.name }}
                    </h3>
                    <span
                      class="text-xs px-2 py-0.5 rounded-full font-medium"
                      :class="{
                        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300':
                          contact.availability_status === 'online',
                        'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300':
                          contact.availability_status === 'busy' ||
                          !contact.availability_status,
                        'text-n-slate-11 bg-n-solid-3 ':
                          contact.availability_status === 'offline',
                      }"
                    >
                      {{
                        contact.availability_status === 'online'
                          ? 'Online'
                          : contact.availability_status === 'busy'
                            ? 'Ocupado'
                            : contact.availability_status === 'offline'
                              ? 'Offline'
                              : 'Ausente'
                      }}
                    </span>
                  </div>
                  <div
                    class="flex items-center gap-3 text-xs text-n-slate-11 mt-1 flex-wrap"
                  >
                    <a
                      v-if="contact.email"
                      :href="`mailto:${contact.email}`"
                      class="flex items-center gap-1 hover:text-woot-500 dark:hover:text-woot-400 truncate max-w-[180px]"
                    >
                      <span class="i-ri-mail-line size-3 shrink-0" />
                      <span class="truncate">{{ contact.email }}</span>
                    </a>
                    <a
                      v-if="contact.phone_number"
                      :href="`tel:${contact.phone_number}`"
                      class="flex items-center gap-1 hover:text-woot-500 dark:hover:text-woot-400"
                    >
                      <span class="i-ri-phone-line size-3 shrink-0" />
                      <span>{{ contact.phone_number }}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </AccordionItem>

          <!-- Seção de Estatísticas (mesmo padrão AccordionItem que Informações do Item) -->
          <AccordionItem
            :title="t('KANBAN.STATISTICS.TITLE')"
            :is-open="isStatisticsOpen"
            compact
            @toggle="isStatisticsOpen = !isStatisticsOpen"
          >
            <div class="p-2">
              <div class="space-y-2">
                <div
                  v-for="stat in itemStatistics"
                  :key="stat.label"
                  class="flex items-center justify-between py-2 px-3 bg-n-solid-2 rounded border border-n-weak"
                >
                  <div class="flex items-center gap-2 min-w-0">
                    <div
                      v-if="stat.icon === 'lock'"
                      class="flex-shrink-0 text-n-slate-11"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="block"
                      >
                        <path d="M12 6v6l4 2" />
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    </div>
                    <fluent-icon
                      v-else
                      :icon="stat.icon"
                      size="14"
                      class="flex-shrink-0 text-n-slate-11"
                    />
                    <span class="text-sm font-medium text-n-slate-12 truncate">
                      {{ stat.label }}
                    </span>
                  </div>
                  <span
                    class="text-sm text-n-slate-12 shrink-0 ml-2 text-right"
                  >
                    {{ stat.value }}
                  </span>
                </div>
              </div>
            </div>
          </AccordionItem>

          <!-- Seção de Informações do Item -->
          <AccordionItem
            :title="
              t('KANBAN.BASIC_DATA_PANEL.ITEM_INFO') || 'Informações do Item'
            "
            :is-open="isItemInfoOpen"
            compact
            @toggle="isItemInfoOpen = !isItemInfoOpen"
          >
            <div class="p-2">
              <!-- Informações básicas do item -->
              <div class="mb-4">
                <div class="flex items-center justify-between mb-2">
                  <h3 class="text-sm font-semibold text-n-slate-12">
                    {{ item.title || item.name || 'Item' }}
                  </h3>
                  <span
                    class="text-xs px-2 py-1 bg-n-alpha-2 text-n-slate-12 rounded"
                  >
                    {{ currentStageName || 'Sem estágio' }}
                  </span>
                </div>
                <!-- Descrição editável -->
                <div class="mt-1">
                  <div
                    v-if="!editingDescription"
                    class="group flex items-start gap-2"
                  >
                    <p
                      v-if="itemDescription || item?.item_details?.description"
                      class="text-sm text-n-slate-11 whitespace-pre-wrap flex-1"
                    >
                      {{ itemDescription || item?.item_details?.description }}
                    </p>
                    <p v-else class="text-sm italic text-n-slate-10 flex-1">
                      Sem descrição
                    </p>
                    <button
                      type="button"
                      class="opacity-0 group-hover:opacity-100 transition-opacity text-xs font-medium text-woot-600 dark:text-woot-400 hover:underline shrink-0"
                      @click="startEditDescription"
                    >
                      Editar
                    </button>
                  </div>
                  <div v-else class="space-y-2">
                    <textarea
                      v-model="descriptionDraft"
                      rows="3"
                      placeholder="Descrição do item..."
                      class="w-full px-3 py-2 text-sm bg-n-solid-2 border border-n-weak rounded-xl focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500 resize-y"
                    />
                    <div class="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        class="px-3 py-1 text-xs font-medium text-n-slate-11 hover:text-n-slate-12"
                        :disabled="savingDescription"
                        @click="cancelEditDescription"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        class="px-3 py-1 text-xs font-medium bg-woot-500 hover:bg-woot-600 text-white rounded-md disabled:opacity-50"
                        :disabled="savingDescription"
                        @click="saveDescription"
                      >
                        {{ savingDescription ? 'Salvando...' : 'Salvar' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Atributos do Contato -->
              <div v-if="contactAttributeValues.length > 0" class="mb-4">
                <h4
                  class="text-xs font-medium text-n-slate-12 uppercase tracking-wide mb-2"
                >
                  Atributos do Contato
                </h4>
                <div class="space-y-2">
                  <div
                    v-for="attr in contactAttributeValues"
                    :key="attr.key"
                    class="flex justify-between items-center py-2 px-3 bg-n-solid-2 rounded border border-n-weak"
                  >
                    <span class="text-sm font-medium text-n-slate-12">
                      {{ attr.displayName }}
                    </span>
                    <span class="text-sm text-n-slate-12">
                      {{ attr.value }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Atributos da Conversa -->
              <div v-if="conversationAttributeValues.length > 0" class="mb-4">
                <h4
                  class="text-xs font-medium text-n-slate-12 uppercase tracking-wide mb-2"
                >
                  Atributos da Conversa
                </h4>
                <div class="space-y-2">
                  <div
                    v-for="attr in conversationAttributeValues"
                    :key="attr.key"
                    class="flex justify-between items-center py-2 px-3 bg-n-solid-2 rounded border border-n-weak"
                  >
                    <span class="text-sm font-medium text-n-slate-12">
                      {{ attr.displayName }}
                    </span>
                    <span class="text-sm text-n-slate-12">
                      {{ attr.value }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Atributos Customizados do Item -->
              <div v-if="itemCustomAttributeValues.length > 0" class="mb-4">
                <h4
                  class="text-xs font-medium text-n-slate-12 uppercase tracking-wide mb-2"
                >
                  Atributos do Item
                </h4>
                <div class="space-y-2">
                  <div
                    v-for="attr in itemCustomAttributeValues"
                    :key="attr.key"
                    class="py-2 px-3 bg-n-solid-2 rounded border border-n-weak"
                  >
                    <div class="flex justify-between items-center mb-1">
                      <span class="text-sm font-medium text-n-slate-12">
                        {{ attr.displayName }}
                      </span>
                    </div>
                    <!-- Se for array, mostra como lista -->
                    <div v-if="attr.isArray" class="mt-2">
                      <div class="flex flex-col gap-1">
                        <span
                          v-for="item in attr.value"
                          :key="item"
                          class="inline-flex items-center px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full w-fit"
                        >
                          {{ item }}
                        </span>
                      </div>
                    </div>
                    <!-- Se não for array, mostra como texto normal -->
                    <div v-else class="text-sm text-n-slate-12">
                      {{ attr.value }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Mensagem quando não há atributos -->
              <div
                v-if="
                  contactAttributeValues.length === 0 &&
                  conversationAttributeValues.length === 0 &&
                  itemCustomAttributeValues.length === 0
                "
                class="text-center py-4"
              >
                <span class="text-sm text-n-slate-11">
                  {{ t('KANBAN.CUSTOM_FIELDS.NO_ATTRIBUTES_FOUND') }}
                </span>
              </div>
            </div>
          </AccordionItem>

          <!-- Seção de Conversa Vinculada -->
          <AccordionItem
            v-if="conversationInfo"
            :title="
              t('KANBAN.BASIC_DATA_PANEL.CONVERSATION') || 'Conversa Vinculada'
            "
            :is-open="isConversationOpen"
            compact
            @toggle="isConversationOpen = !isConversationOpen"
          >
            <div class="p-2">
              <div
                class="bg-n-solid-2 rounded-xl border border-n-weak/30 p-3 cursor-pointer hover:bg-n-alpha-2/50 transition-colors"
                @click="
                  $emit(
                    'navigate-to-conversation',
                    $event,
                    conversationInfo.display_id
                  )
                "
                @contextmenu="
                  $emit('context-menu', $event, conversationInfo.id)
                "
              >
                <!-- Contato -->
                <div
                  v-if="conversationInfo.contact"
                  class="flex items-center gap-3 mb-2"
                >
                  <img
                    v-if="conversationInfo.contact.thumbnail"
                    :src="conversationInfo.contact.thumbnail"
                    :alt="conversationInfo.contact.name"
                    class="w-8 h-8 rounded-full object-cover"
                  />
                  <div
                    v-else
                    class="w-8 h-8 bg-n-alpha-2 rounded-full flex items-center justify-center"
                  >
                    <span class="text-xs font-medium text-n-slate-11">
                      {{
                        conversationInfo.contact.name
                          ?.charAt(0)
                          ?.toUpperCase() || '?'
                      }}
                    </span>
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center justify-between">
                      <div class="font-semibold text-sm text-n-slate-12">
                        <span class="text-xs text-n-slate-11 mr-1">#{{ conversationInfo.display_id }}</span>
                        {{
                          conversationInfo.contact.name ||
                          t('KANBAN.ITEM_CONVERSATION.NO_CONTACT')
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center gap-1">
                    <!-- Status -->
                    <span
                      class="text-xs px-2 py-0.5 rounded-full font-medium"
                      :class="{
                        'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300':
                          conversationInfo.status === 'open',
                        'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300':
                          conversationInfo.status === 'pending',
                        'text-n-slate-12 bg-n-solid-3 ':
                          conversationInfo.status === 'resolved',
                        'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300':
                          conversationInfo.status === 'snoozed',
                      }"
                    >
                      {{ conversationStatusLabel }}
                    </span>
                    <!-- Unread count -->
                    <span
                      v-if="conversationInfo.unread_count > 0"
                      class="flex items-center justify-center h-5 min-w-[1rem] px-1.5 text-xs font-medium bg-red-500 text-white rounded-full"
                    >
                      {{
                        conversationInfo.unread_count > 9
                          ? '9+'
                          : conversationInfo.unread_count
                      }}
                    </span>
                  </div>
                </div>

                <!-- Labels -->
                <div
                  v-if="
                    conversationInfo.label_list &&
                    conversationInfo.label_list.length > 0
                  "
                  class="flex flex-wrap gap-1"
                >
                  <span
                    v-for="label in conversationInfo.label_list"
                    :key="label"
                    class="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full"
                  >
                    {{ label }}
                  </span>
                </div>
              </div>
            </div>
          </AccordionItem>

          <!-- Banner: confirma envio do follow-up automatico nesta etapa -->
          <div
            v-if="scheduledFollowUpDispatch"
            class="mx-3 my-2 flex items-start gap-2 rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-3"
          >
            <span
              class="i-ri-checkbox-circle-line size-[18px] text-green-600 dark:text-green-400 shrink-0 mt-0.5"
            />
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-green-800 dark:text-green-200">
                {{
                  t('KANBAN.BASIC_DATA_PANEL.SCHEDULED_FOLLOW_UP.SENT_TITLE')
                }}
              </p>
              <p class="text-xs text-green-700 dark:text-green-300 mt-0.5">
                {{
                  t('KANBAN.BASIC_DATA_PANEL.SCHEDULED_FOLLOW_UP.SENT_AT', {
                    sentAt: scheduledFollowUpSentAtLabel,
                  })
                }}
              </p>
            </div>
          </div>

          <!-- Seção de Dados Adicionais -->
          <AccordionItem
            :title="t('KANBAN.CUSTOM_FIELDS.TITLE') || 'Dados Adicionais'"
            :is-open="isCustomFieldsOpen"
            compact
            @toggle="isCustomFieldsOpen = !isCustomFieldsOpen"
          >
            <div class="p-2">
              <KanbanCustomFieldsTab
                :item="item"
                :is-stacklab="isStacklab"
                @update:item="$emit('update:item', $event)"
                @item-updated="$emit('item-updated')"
              />
            </div>
          </AccordionItem>

          <div
            class="rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-3"
          >
            <div class="flex items-start gap-2">
              <span
                class="i-ri-map-pin-line size-8 mt-0.5 text-green-700 dark:text-green-300"
              />
              <div>
                <p
                  class="text-sm font-semibold text-green-800 dark:text-green-200"
                >
                  {{
                    hasLocationData
                      ? 'Dados de localização cadastrados'
                      : 'Rastreie este item na visão de Mapa'
                  }}
                </p>
                <p
                  v-if="!hasLocationData"
                  class="text-xs text-green-700 dark:text-green-300 mt-1"
                >
                  Cadastre os dados de localização em Dados Adicionais para
                  acompanhar este item no mapa.
                </p>
                <div
                  v-else
                  class="text-xs text-green-700 dark:text-green-300 mt-1 space-y-1"
                >
                  <p v-if="props.item?.item_details?.location_cep">
                    <span class="font-semibold">CEP:</span>
                    {{ props.item.item_details.location_cep }}
                  </p>
                  <p v-if="props.item?.item_details?.location_address">
                    <span class="font-semibold">Endereço:</span>
                    {{ props.item.item_details.location_address }}
                  </p>
                </div>
                <button
                  type="button"
                  class="mt-3 inline-flex items-center rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 transition-colors"
                  @click="showLocationModal = true"
                >
                  {{
                    hasLocationData
                      ? 'Editar dados de localização'
                      : 'Adicionar dados de localização'
                  }}
                </button>
              </div>
            </div>
          </div>

          <div
            v-if="scheduledFollowUpDispatch"
            class="rounded-xl border border-[#D8B4FE] dark:border-[#6B21A8] bg-[#F3E8FF] dark:bg-[#3B0764]/30 p-3"
          >
            <div class="flex items-start gap-2">
              <span
                class="i-ri-checkbox-circle-line size-5 mt-0.5 text-[#7E22CE] dark:text-[#C084FC]"
              />
              <div>
                <p
                  class="text-sm font-semibold text-[#581C87] dark:text-[#E9D5FF]"
                >
                  {{
                    t('KANBAN.BASIC_DATA_PANEL.SCHEDULED_FOLLOW_UP.SENT_TITLE')
                  }}
                </p>
                <p class="text-xs text-[#6B21A8] dark:text-[#D8B4FE] mt-1">
                  {{
                    t('KANBAN.BASIC_DATA_PANEL.SCHEDULED_FOLLOW_UP.SENT_AT', {
                      sentAt: scheduledFollowUpSentAtLabel,
                    })
                  }}
                </p>
                <p
                  v-if="isScheduledFollowUpHistory"
                  class="text-xs text-[#6B21A8] dark:text-[#D8B4FE] mt-1"
                >
                  Foi enviado na etapa
                  <span class="font-semibold">{{
                    scheduledFollowUpDispatchStageKey
                  }}</span>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Modal
      v-model:show="showLocationModal"
      :on-close="() => (showLocationModal = false)"
      size="tiny"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium text-n-slate-12">
          Dados de localização
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">
          Preencha o CEP ou o endereço para rastrear este item na visão de mapa.
        </p>

        <div class="mt-4 space-y-3">
          <div>
            <label
              for="kanban-location-cep"
              class="block text-sm font-medium text-n-slate-12 mb-1"
            >
              CEP
            </label>
            <input
              id="kanban-location-cep"
              v-model="locationCep"
              type="text"
              placeholder="Ex: 01001-000"
              class="w-full rounded-xl border border-n-weak bg-n-solid-3 px-3 py-2 text-sm text-n-slate-12"
            />
            <div class="mt-2">
              <span v-if="loadingCep" class="text-xs text-n-slate-11">
                Buscando endereço...
              </span>
              <span
                v-else-if="cepLookupError"
                class="text-xs text-red-600 dark:text-red-400"
              >
                {{ cepLookupError }}
              </span>
            </div>
          </div>

          <div>
            <label
              for="kanban-location-address"
              class="block text-sm font-medium text-n-slate-12 mb-1"
            >
              Endereço
            </label>
            <input
              id="kanban-location-address"
              v-model="locationAddress"
              type="text"
              placeholder="Ex: Rua Exemplo, 123 - Centro"
              class="w-full rounded-xl border border-n-weak bg-n-solid-3 px-3 py-2 text-sm text-n-slate-12"
            />
          </div>
        </div>

        <div class="mt-6 flex justify-end">
          <button
            type="button"
            class="px-4 py-2 rounded-xl text-sm text-white bg-woot-500 hover:bg-woot-600 disabled:opacity-60"
            :disabled="savingLocation"
            @click="saveLocationData"
          >
            {{ savingLocation ? 'Salvando...' : 'Confirmar' }}
          </button>
        </div>
      </div>
    </Modal>

    <Modal v-model:show="showOrderbumpModal" :on-close="closeOrderbumpModal">
      <div class="p-6">
        <div
          v-if="orderbumpMessageSent"
          class="flex flex-col items-center justify-center py-8 text-center"
        >
          <div
            class="w-24 h-24 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center"
          >
            <span
              class="i-ri-check-line size-[52px] text-green-600 dark:text-green-400"
            />
          </div>
          <h3 class="mt-5 text-xl font-medium text-n-slate-12">
            {{ t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.SUCCESS_SENT') }}
          </h3>
          <button
            type="button"
            class="mt-6 px-4 py-2 rounded-xl text-sm text-white bg-woot-500 hover:bg-woot-600"
            @click="closeOrderbumpModal"
          >
            {{ t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.CANCEL') }}
          </button>
        </div>
        <div v-else>
          <h3 class="text-lg font-medium text-n-slate-12">
            {{ t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.MODAL_TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            {{
              selectedOrderbumpProduct
                ? `#${selectedOrderbumpProduct.id} - ${selectedOrderbumpProduct.title || selectedOrderbumpProduct.description}`
                : ''
            }}
          </p>

          <div class="mt-4">
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              {{ t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.MESSAGE_LABEL') }}
            </label>
            <textarea
              v-model="orderbumpMessage"
              rows="5"
              class="w-full rounded-xl border border-n-weak bg-n-solid-3 px-3 py-2 text-sm text-n-slate-12"
              :placeholder="
                t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.MESSAGE_PLACEHOLDER')
              "
            />
          </div>

          <div class="mt-6 flex justify-end gap-2">
            <button
              type="button"
              class="px-4 py-2 rounded-xl border border-n-weak text-sm text-n-slate-12 hover:bg-n-alpha-2"
              :disabled="sendingOrderbumpMessage"
              @click="closeOrderbumpModal"
            >
              {{ t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.CANCEL') }}
            </button>
            <button
              type="button"
              class="px-4 py-2 rounded-xl text-sm text-white bg-woot-500 hover:bg-woot-600 disabled:opacity-60"
              :disabled="sendingOrderbumpMessage || !orderbumpMessage.trim()"
              @click="sendOrderbumpMessage"
            >
              {{
                sendingOrderbumpMessage
                  ? t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.SENDING')
                  : t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.SEND')
              }}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
::v-deep {
  .contact--profile {
    @apply pb-3 border-b border-solid border-n-weak;
  }
}
</style>
